import type { AxisFilterChip, AxisFilterFieldDef } from "./AxisFilterBar";

// AxisFilterBar 칩과 URL query param 사이의 코덱이다. 축 이름은 화면마다 다르므로 축→키 맵을 받는다.
// 값 검증은 축 정의가 이미 선언한 값(`options` = 닫힌 집합)에서 만든다. 특정 축 이름에 의존하는
// 분기를 두지 않는다. 그래야 새 닫힌 집합 축에도 같은 규칙이 자동으로 적용된다.
export type AxisFilterParamMap<K extends string> = Readonly<Record<K, string>>;

function isAllowedValue<K extends string>(field: AxisFilterFieldDef<K>, value: string): boolean {
  return field.options === undefined || field.options.some((option) => option.value === value);
}

/**
 * URL 에서 칩을 읽는다.
 *
 * URL 은 외부 입력이다. 축 선언 순서로 훑어 칩 순서를 정규화하고, 공백만 있는 값과 닫힌 집합을
 * 벗어난 값은 버린다.
 *
 * Args:
 *   params: 현재 query params.
 *   fields: 이 화면의 축 정의.
 *   paramByField: 축 → query 키 맵.
 *
 * Returns:
 *   축 선언 순서로 정렬된 칩.
 */
export function readAxisFilterChips<K extends string>(
  params: URLSearchParams,
  fields: readonly AxisFilterFieldDef<K>[],
  paramByField: AxisFilterParamMap<K>,
): AxisFilterChip<K>[] {
  const chips: AxisFilterChip<K>[] = [];
  for (const field of fields) {
    const value = params.get(paramByField[field.key])?.trim();
    if (value && isAllowedValue(field, value)) {
      chips.push({ key: field.key, value });
    }
  }
  return chips;
}

/**
 * 칩 전체를 다시 쓴다(full replace).
 *
 * 값이 빈 축은 키를 빼서 불필요한 키가 없는 URL 을 유지한다. 입력 params 는 변경하지 않는다.
 *
 * Args:
 *   params: 현재 query params.
 *   fields: 이 화면의 축 정의.
 *   paramByField: 축 → query 키 맵.
 *   chips: 기록할 칩 전체.
 *
 * Returns:
 *   칩 키만 갱신된 새 params.
 */
export function writeAxisFilterChips<K extends string>(
  params: URLSearchParams,
  fields: readonly AxisFilterFieldDef<K>[],
  paramByField: AxisFilterParamMap<K>,
  chips: readonly AxisFilterChip<K>[],
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const param of Object.values<string>(paramByField)) {
    next.delete(param);
  }
  for (const chip of chips) {
    const field = fields.find((candidate) => candidate.key === chip.key);
    const value = chip.value.trim();
    if (field !== undefined && value !== "" && isAllowedValue(field, value)) {
      next.set(paramByField[chip.key], value);
    }
  }
  return next;
}
